import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CourseProvider } from "@/contexts/CourseContext";
import { AuthProvider } from "@/contexts/AuthContext";
import CourseAssistant from "@/components/CourseAssistant";
import OnboardingTutorial from "@/components/OnboardingTutorial";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Onboarding from "./pages/Onboarding";
import RequestDemo from "./pages/RequestDemo";
import Course from "./pages/Course";
import LearnCourse from "./pages/LearnCourse";
import LearnNodeContent from "./pages/LearnNodeContent";
import LearnerDashboard from "./pages/LearnerDashboard";
import InstructorDashboard from "./pages/InstructorDashboard";
import ManageNodeLesson from "./pages/ManageNodeLesson";
import ManageNodeExam from "./pages/ManageNodeExam";
import ManageNodeSlides from "./pages/ManageNodeSlides";
import Workspace from "./pages/Workspace";
import NotFound from "./pages/NotFound";
import CreateCoursePage from "./pages/CreateCoursePage";
import Create from "./pages/Create";
import SkillProfile from "./pages/SkillProfile";
import PublicSkillProfile from "./pages/PublicSkillProfile";
import DesignerGuard from "./components/DesignerGuard";
import StudentDetail from "./pages/StudentDetail";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24h - keep cache for persistence
    },
  },
});

const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
});

// Component to conditionally render the assistant based on route
const ConditionalAssistant = () => {
  const location = useLocation();
  
  // Don't show assistant on public pages
  const publicPaths = ['/', '/auth', '/reset-password', '/request-demo', '/onboarding', '/privacy', '/terms'];
  const isPublicPage = publicPaths.some(path => location.pathname === path);

  // Node lesson pages have a dedicated embedded tutor chat — suppress the global one
  const isNodePage = /^\/learn\/courses\/[^/]+\/nodes\//.test(location.pathname);

  if (isPublicPage || isNodePage) return null;
  
  return <CourseAssistant />;
};

const App = () => (
  <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
    <TooltipProvider>
      <AuthProvider>
      <CourseProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/create" element={<DesignerGuard><Create /></DesignerGuard>} />
              <Route path="/dashboard" element={<DesignerGuard><Dashboard /></DesignerGuard>} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/pricing" element={<Navigate to="/#platform-capabilities" replace />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/request-demo" element={<RequestDemo />} />
              <Route path="/workspace/:id/create" element={<DesignerGuard><CreateCoursePage /></DesignerGuard>} />
              <Route path="/workspace/:id" element={<DesignerGuard><Workspace /></DesignerGuard>} />
              <Route path="/project/:id" element={<DesignerGuard><Course /></DesignerGuard>} />
              <Route path="/project/:projectId/node/:nodeId/lesson" element={<DesignerGuard><ManageNodeLesson /></DesignerGuard>} />
              <Route path="/project/:projectId/node/:nodeId/exam" element={<DesignerGuard><ManageNodeExam /></DesignerGuard>} />
              <Route path="/project/:projectId/node/:nodeId/slides" element={<DesignerGuard><ManageNodeSlides /></DesignerGuard>} />
              <Route path="/instructor/:id" element={<DesignerGuard><InstructorDashboard /></DesignerGuard>} />
              <Route path="/instructor/:id/students/:studentId" element={<DesignerGuard><StudentDetail /></DesignerGuard>} />
              <Route path="/learn/courses/:id" element={<LearnCourse />} />
              <Route path="/learn/courses/:id/nodes/:nodeId" element={<LearnNodeContent />} />
              <Route path="/learn" element={<LearnerDashboard />} />
              <Route path="/profile" element={<SkillProfile />} />
              <Route path="/p/:slug" element={<PublicSkillProfile />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
          </Routes>
          <ConditionalAssistant />
          <OnboardingTutorial />
        </BrowserRouter>
      </CourseProvider>
      </AuthProvider>
    </TooltipProvider>
  </PersistQueryClientProvider>
);

export default App;
